import React, {useEffect, useState} from 'react';
import {PREPARED_CART_ICON} from '../../Constants';
import {
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    Dropdown,
    IconButton,
    Label,
    MessageBar,
    MessageBarButton,
    MessageBarType,
    PrimaryButton,
    Stack,
    Text,
    TooltipHost,
    useTheme
} from '@fluentui/react';
import AutocompleteSearchBox, {ISuggestionItem} from '../Common/AutocompleteSearchBox';
import SuggestionPill from '../Common/SuggestionPill';
import ContactDetailsModal from './ContactDetailsModal';
import {useGetCompanySuggestionsQuery, useGetContactsForCompanyQuery, useLazyGetCompaniesQuery} from '../../store/Api';
import {CompanySuggestionReason, ICompany, ICompanySuggestion, IContact} from '../../models/Company';

/** Icon per suggestion reason (see ICompanySuggestion.reason). */
const SUGGESTION_ICONS: Record<CompanySuggestionReason, string> = {
    storefrontOrders: 'Shop',
    preparedCarts: PREPARED_CART_ICON,
    orders: 'Trending12',
    recent: 'History'
};

class CompanyOption implements ISuggestionItem {
    constructor(private readonly company: ICompany) {}
    getSearchText = () => this.company.companyName ?? '';
    getKey = () => this.company.id ?? '';
    getSuggestionItem() {
        return <div className='suggestionItem'>{this.company.companyName}</div>;
    }
}

interface ICompanyContactPickerDialogProps {
    isOpen: boolean;
    /** Scopes the suggested companies (top orders, recent prepared carts) to this storefront. */
    storefrontId?: string;
    onDismiss: () => void;
    /** The chosen company and, when one was picked, its contact. */
    onSave: (company: ICompany, contact?: IContact) => void | Promise<void>;
}

/**
 * The same company + contact selection the prepared cart panel uses (suggestions, pill-style
 * single select, add-a-contact shortcut), in a dialog, for attaching a customer to a workspace.
 */
const CompanyContactPickerDialog: React.FC<ICompanyContactPickerDialogProps> = (props) => {
    const theme = useTheme();
    const [company, setCompany] = useState<ICompany>();
    const [contactId, setContactId] = useState<string>();
    const [results, setResults] = useState<ICompany[]>([]);
    const [options, setOptions] = useState<CompanyOption[]>();
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showContactEditor, setShowContactEditor] = useState(false);
    const [search] = useLazyGetCompaniesQuery();
    const suggestions = useGetCompanySuggestionsQuery({storefrontId: props.storefrontId || undefined, limit: 6}, {skip: !props.isOpen || !!company});
    const contacts = useGetContactsForCompanyQuery({companyId: company?.id ?? ''}, {skip: !company?.id});

    // Fresh start each time the dialog opens.
    useEffect(() => {
        if (!props.isOpen) return;
        setCompany(undefined);
        setContactId(undefined);
        setOptions(undefined);
        setSaving(false);
    }, [props.isOpen]);

    // One contact is the obvious pick; select it so the common case is a single click on Attach.
    useEffect(() => {
        if (contacts.data?.items.length === 1) setContactId(contacts.data.items[0].id);
    }, [contacts.data]);

    const onType = async (value?: string) => {
        if (!value || value.trim().length < 2) {
            setOptions(undefined);
            return;
        }
        setSearching(true);
        try {
            const result = await search({query: {query: value}}).unwrap();
            setResults(result?.items ?? []);
            setOptions((result?.items ?? []).map(c => new CompanyOption(c)));
        } finally {
            setSearching(false);
        }
    };

    const pick = (next: ICompany) => {
        setCompany(next);
        setContactId(undefined);
        setOptions(undefined);
    };

    const save = async () => {
        if (!company) return;
        setSaving(true);
        try {
            await props.onSave(company, contacts.data?.items.find(c => c.id === contactId));
        } finally {
            setSaving(false);
        }
    };

    const noContacts = !!company?.id && contacts.data !== undefined && contacts.data.items.length === 0;
    const contactOptions = (contacts.data?.items ?? []).map(c => ({
        key: c.id ?? '',
        text: `${[c.firstName, c.lastName].filter(Boolean).join(' ')}${c.emailAddress ? ` - ${c.emailAddress}` : ''}`
    }));

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onDismiss}
            minWidth={520}
            dialogContentProps={{
                type: DialogType.normal,
                title: 'Attach Company and Contact',
                subText: 'The company and contact are attached to this workspace and, when one exists, its prepared cart.'
            }}
            modalProps={{isBlocking: false}}>
            <AutocompleteSearchBox
                label='Customer Company'
                required={true}
                placeholder='Search companies'
                onChange={async (_, value) => await onType(value)}
                onSuggestionClicked={(suggestion) => {
                    if (typeof suggestion === 'string') return;
                    const match = results.find(c => c.id === suggestion.getKey());
                    pick(match ?? {id: suggestion.getKey(), companyName: suggestion.getSearchText()});
                }}
                suggestions={options}
                inProgress={searching}
                debounceTime={400}
                layerSuggestions={true}
                selectedValue={company ? {key: company.id ?? '', text: company.companyName ?? '', iconName: 'CityNext2'} : undefined}
                onClearSelection={() => { setCompany(undefined); setContactId(undefined); }}
            />
            {!company && (suggestions.data?.length ?? 0) > 0 && suggestions.data &&
                <Stack horizontal wrap verticalAlign='center' tokens={{childrenGap: '0.5em'}} styles={{root: {marginTop: '0.5em'}}}>
                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>Suggested</Text>
                    {suggestions.data.map((s: ICompanySuggestion) =>
                        <SuggestionPill
                            key={s.companyId}
                            text={s.companyName ?? s.companyId}
                            iconName={SUGGESTION_ICONS[s.reason] ?? 'CityNext2'}
                            tooltip={<div>{s.companyName}{s.detail && <div>{s.detail}</div>}</div>}
                            onClick={() => pick({id: s.companyId, companyName: s.companyName})}
                        />)}
                </Stack>}
            {company && !noContacts &&
                <Dropdown
                    label='Contact'
                    onRenderLabel={(dropdownProps) =>
                        <div style={{display: 'inline-flex'}}>
                            <Label>{dropdownProps?.label}</Label>
                            <TooltipHost content={`Add a contact to ${company.companyName ?? 'this company'}`}>
                                <IconButton iconProps={{iconName: 'AddFriend'}} ariaLabel='Add a contact' onClick={() => setShowContactEditor(true)} />
                            </TooltipHost>
                        </div>}
                    placeholder={contacts.isLoading ? 'Loading contacts…' : 'Select a contact'}
                    options={contactOptions}
                    selectedKey={contactId ?? null}
                    disabled={contacts.isLoading}
                    onChange={(_, option) => setContactId(option?.key as string | undefined)}
                />}
            {noContacts &&
                <MessageBar messageBarType={MessageBarType.info} styles={{root: {marginTop: '0.5em'}}}
                    actions={<div><MessageBarButton onClick={() => setShowContactEditor(true)}>Add Contact</MessageBarButton></div>}>
                    {company?.companyName ?? 'This company'} has no contacts. Add one to deliver a prepared cart.
                </MessageBar>}
            {company?.id && showContactEditor &&
                <ContactDetailsModal
                    isOpen={showContactEditor}
                    defaultCompany={company.id}
                    onDismiss={(saved, newContactId) => {
                        setShowContactEditor(false);
                        if (!saved) return;
                        if (!contacts.isUninitialized) contacts.refetch();
                        if (newContactId) setContactId(newContactId);
                    }}
                />}
            <DialogFooter>
                <PrimaryButton text='Attach' iconProps={{iconName: 'ContactLink'}} disabled={!company || saving} onClick={() => void save()} />
                <DefaultButton text='Cancel' onClick={props.onDismiss} />
            </DialogFooter>
        </Dialog>
    );
};

export default CompanyContactPickerDialog;
